import 'cross-fetch/polyfill'

import { getCorrelationId, leaveBreadcrumb } from './debug'
import { createErrorContext, RequestError } from './errors'
import { isClient } from './isClient'
import { type ProxySettings, proxyAgent } from './proxyAgent'
import { queryAppend } from './query'

// A full definition of fetch is not really necessary
declare const fetch: any

// Avoid acquiring response type for now, it is unnecessary
const getContent = async (response: any) => {
	const contentType = response.headers.get('content-type') || ''

	if (!contentType) {
		return undefined
	}

	if (contentType.startsWith('application/json') || contentType.startsWith('application/problem+json')) {
		const text = await response.text()
		return text === '' ? undefined : JSON.parse(text)
	}

	if (contentType.startsWith('text/')) {
		return response.text()
	}

	// node does not support blob, browser does not support streams
	if (isClient) {
		// Use regular blob
		return response.blob()
	}
	// Create wrapper for node that includes the stream
	// Getting data like this should not be isomorphic anyways
	return {
		stream: response.body,
		type: contentType,
	}
}

export type Method = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'

export type Options = {
	/** URL of request. */
	url: string
	/** Method of request. */
	method?: Method
	/**
	 * Data to be sent.
	 * Must be serializable as JSON or URLSearchParams for POST/PUT and serializable
	 * as plain query for GET/DELETE.
	 */
	data?: any
	/** Headers for request. Accept/Content-type headers are automatically added. */
	headers?: { [key: string]: string }
	/** @deprecated Now an alias for `data` */
	query?: any
	/** Proxy settings, creates a custom agent on server */
	proxy?: ProxySettings
	/** Browser cookie policy. Defaults to omitting cookies. */
	credentials?: 'omit' | 'same-origin' | 'include'
	/** Should `data` be included as `api.request` on errors. */
	logRequestData?: boolean
	/** Return the raw text response instead of parsing the response body. */
	rawText?: boolean
}

/**
 * Wrapper around isomorphic fetch. Automatically handles status-codes and
 * content decoding.
 *
 * Handles `application/json` as JSON, `text/*` as text and everything else as
 * blob/stream.
 * @param options
 */
export const request = async <T = void>({
	url,
	method = 'GET',
	data,
	headers = {},
	query,
	proxy,
	credentials = 'omit',
	logRequestData = false,
	rawText = false,
}: Options): Promise<T> => {
	method = method.toUpperCase() as Method
	// Backwards compatibility
	if (!data && query) {
		data = query
	}
	const correlationId = headers['X-Correlation-ID'] ?? getCorrelationId()
	const errorContext = createErrorContext(
		{
			url,
			method,
			data: logRequestData ? data : '<omitted>',
			correlationId,
		},
		request,
	)

	const methodHasBody = method !== 'GET' && method !== 'DELETE'
	const isFormBody = data instanceof URLSearchParams

	const fullUrl = data && !methodHasBody ? queryAppend(url, data) : url
	const body = data && methodHasBody ? (isFormBody ? data.toString() : JSON.stringify(data)) : undefined

	headers.Accept = 'application/json, text/*;q=0.9, */*;q=0.8'
	headers['Cache-Control'] = 'no-cache'
	headers.Pragma = 'no-cache'
	headers['X-Correlation-ID'] = correlationId

	if (data && methodHasBody) {
		headers['Content-Type'] = isFormBody ? 'application/x-www-form-urlencoded' : 'application/json'
	}

	let response: Response
	try {
		response = await fetch(fullUrl, {
			method,
			headers,
			body,
			// Allow including an http-agent on server for proxy
			agent: proxyAgent(proxy),
			credentials,
			redirect: 'manual',
		})
	} catch (error: any) {
		leaveBreadcrumb({
			type: 'http',
			category: 'fetch',
			data: { url, method, status_code: 0, correlationId },
			level: 'error',
		})
		throw RequestError.fromError(error, errorContext)
	}

	if (response.ok) {
		leaveBreadcrumb({
			type: 'http',
			category: 'fetch',
			data: { url, method, status_code: response.status, correlationId },
		})
		if (rawText) {
			return response.text() as unknown as T
		}
		return getContent(response)
	}

	if ((response.status === 301 || response.status === 302) && response.headers.has('Location')) {
		leaveBreadcrumb({
			type: 'http',
			category: 'fetch',
			data: { url, method, status_code: response.status, correlationId },
		})
		return {
			location: response.headers.get('Location'),
		} as any
	}

	leaveBreadcrumb({
		type: 'http',
		category: 'fetch',
		data: { url, method, status_code: response.status, correlationId },
		level: response.status < 500 ? 'warning' : 'error',
	})

	let responseData: any

	try {
		responseData = await getContent(response)
	} catch {
		// Suppress error
	}

	throw RequestError.fromResponse(response, responseData, errorContext)
}
